import {Plugins, Component, Expressions} from '@packages/systems/wfdl';
import {anyPass} from '@packages/utilities/fp/utils';
import {Err, Ok, type Result} from '@packages/utilities/fp/result';
import {
  isInsideDynamoItem,
  isInsideAnyCommerceDynListItem,
  isInsideUnboundDynList,
  containsDynamoWrapperWithDynContextFilter,
  containsDynConditionedWithDynContextCondition,
  containsNestedDynamoWrapper,
  containsDynamoWrapper,
  containsItemRefSetBoundList,
  isPaginatedDynamoWrapperElement,
  isInsideRichTextFigure,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {isLeaf} from '@packages/systems/constraints';

import {
  isUserComponentRoot,
  isChildrenBound,
  isInsideCommerceCartWrapper,
  isInsideCommerceCheckoutFormContainer,
  isInside,
  isInsideSearchForm,
  isInsidePasswordPageForm,
  isInsideSearchResultWrapper,
  isInsideSearchResultItem,
  contains,
  isComponentInst,
  getComponentInstRootElement,
  isUserComponentInst,
  isOptimizationVariationElement,
  componentContainsHtmlEmbed,
  getSlotAnchorPath,
  getSlotOwnerComponent,
  getSlotDefaultOwnerComponent,
  expressionContainsSpecificElement,
} from '@packages/utilities/platform';
import {findElementInComponentTree} from '@packages/utilities/core-elements';
import {
  isCommerceCartWrapper,
  isSearchForm,
  isPagination,
  isCommercePaypalCheckoutFormContainer,
  isDynamoList,
  isDynamoWrapper,
  isLocalesWrapper,
  isLocalesList,
  isSearchBound,
  isSearchResultWrapper,
  isSearchInput,
  isPasswordPageButton,
  isPasswordPageInput,
  isPasswordPageForm,
  isSearchResultList,
  isFormWrapper,
  isFormForm,
  isSearchButton,
  isCommerceCheckoutFormContainer,
  isCommerceOrderConfirmationContainer,
  isUserAccountWrapper,
  isSlot,
  isComponentOnlyDropTarget,
  isRichText,
} from '@packages/utilities/elementTypeUtils';
import {
  isStandard,
  selectedElementContainsSlot,
} from '@packages/utilities/platform-elements';
import {designerTranslation} from '@packages/systems/designer-translations';

import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  type UserComponentUsage,
  isConstrainedByUserComponentUsages,
  isInsideStaticRichText,
} from '@packages/systems/user-components/usages';
import {getPluginName} from '@packages/systems/wfdl-Qualified';
import {
  buildForbiddenRichTextNestingError,
  containsRichTextSlotOverride,
  findRichTextForbiddenElement,
  getRichTextForbiddenElementDisplayName,
} from '@packages/utilities/rich-text-validation';

type RichTextValidationFlags = {
  isComponentSlotDefaultsEnabled?: boolean;
};

type SlotDefaultAuthoringContext = {
  isEditingComponentDefinition: boolean;
  isEnabled: boolean;
  rawParent: ExpressionElement;
  rawChild: ExpressionElement | null;
  currentComponent: Component.ComponentType | null;
  componentMap: Component.ComponentMap;
};

function isSlotDefaultTarget({
  isEditingComponentDefinition,
  isEnabled,
  rawParent,
  rawChild,
  currentComponent,
  componentMap,
}: SlotDefaultAuthoringContext): boolean {
  if (!isEditingComponentDefinition || !isEnabled) {
    return false;
  }

  if (!currentComponent || !rawChild) {
    return false;
  }

  if (isSlot(rawParent)) {
    // A Slot reached through a materialized tree has had its field binding replaced
    // by the content, so the field id comes from the component's authored copy.
    const declaresSlot = (component: Component.ComponentType): boolean => {
      const authored =
        Expressions.getElementById(
          Component.getRender(component),
          rawParent.id
        ) ?? rawParent;
      return Boolean(getSlotAnchorPath(authored, component));
    };

    // A Slot on a nested instance is authorable too: while editing a definition the
    // write promotes into that definition's own default.
    const slotOwner = getSlotOwnerComponent(componentMap, rawParent);
    if (
      declaresSlot(currentComponent) ||
      (slotOwner && declaresSlot(slotOwner))
    ) {
      return true;
    }
  }

  // A promoted placement resolves the Slot to the instance that owns it, so the
  // parent is that instance's render and no longer answers isSlot. Living in a
  // default is what still identifies the drop.
  return Boolean(getSlotDefaultOwnerComponent(componentMap, rawParent));
}

/**
 * Validates that elements being added to a component used inside Rich Text
 * don't contain forbidden element types (Collection Lists).
 */
function validateStaticRichTextNesting(
  constrainingUserComponents: Array<UserComponentUsage>,
  child: ExpressionElement,
  componentMap: Component.ComponentMap
): Result<string, ExpressionElement> | undefined {
  const {t} = designerTranslation;
  if (!isInsideStaticRichText(constrainingUserComponents)) {
    return undefined;
  }

  // Check for forbidden Collection Lists.
  // Uses findRichTextForbiddenElement to recursively check component instances
  const forbiddenElement = findRichTextForbiddenElement(child, componentMap);

  if (forbiddenElement) {
    return buildForbiddenRichTextNestingError({
      child,
      forbiddenElement,
      ancestorLabel: 'rich text',
      translate: t,
    });
  }

  return undefined;
}

export function validateNesting(
  plugins: Plugins.PluginsType,
  constrainingUserComponents: Array<UserComponentUsage>,
  componentMap: Component.ComponentMap,
  rawParent: ExpressionElement,
  featureFlags: RichTextValidationFlags = {},
  isEditingComponentDefinition: boolean = false,
  rawChild: ExpressionElement,
  currentComponent: Component.ComponentType
): Result<string, ExpressionElement> {
  const {t} = designerTranslation;
  const root = Component.getRender(currentComponent);

  const targetsSlotDefault = isSlotDefaultTarget({
    isEditingComponentDefinition,
    isEnabled: featureFlags.isComponentSlotDefaultsEnabled === true,
    rawParent,
    rawChild,
    currentComponent,
    componentMap,
  });

  // Use the raw child so Slots from expanded component instances do not block valid defaults.
  const isAuthoringSlotDefault =
    targetsSlotDefault && !selectedElementContainsSlot(rawChild);

  const isInRichTextContext =
    isInside(isRichText)(root)(rawParent) ||
    isInsideStaticRichText(constrainingUserComponents);

  // Slot targets expand to the component root, so the expanded parent is not
  // itself a Slot. The raw instance identifies that path; pasted instances can
  // also already carry a SlotContent override.
  if (
    isInRichTextContext &&
    !isAuthoringSlotDefault &&
    (isUserComponentInst(componentMap, rawParent) ||
      containsRichTextSlotOverride([rawChild], componentMap))
  ) {
    return Err("Component slots can't be populated in Rich Text.");
  }

  if (isSlot(rawParent) && !isAuthoringSlotDefault) {
    return Err(
      'To place elements into a Slot, go to an instance of this Component.'
    );
  }

  // Keyed on the target rather than isAuthoringSlotDefault: a promoted placement
  // hands us the instance that owns the Slot, so the check above cannot see it,
  // and a child this rule rejects is exactly a child that clears that flag.
  if (
    targetsSlotDefault &&
    !isUserComponentInst(componentMap, rawChild) &&
    !isLibraryComponentInstance(rawChild, componentMap, plugins)
  ) {
    return Err('Only Components are allowed as Slot defaults.');
  }

  if (
    isComponentOnlyDropTarget(rawParent) &&
    !isComponentInst(componentMap)(rawChild)
  ) {
    return Err('Only Components are allowed in a Page Slot.');
  }

  // Block components with custom code from being dropped into a wrapped page slot
  if (
    isComponentOnlyDropTarget(rawParent) &&
    isComponentInst(componentMap)(rawChild)
  ) {
    // Check if page slot is inside an optimization variation wrapper
    const closestVariation = Expressions.closestElement(
      root,
      rawParent.id,
      isOptimizationVariationElement
    );

    if (
      closestVariation &&
      componentContainsHtmlEmbed(rawChild, componentMap)
    ) {
      return Err(
        'Components with custom code cannot be placed in an optimized Page Slot.'
      );
    }
  }

  if (
    contains(rawChild, isComponentOnlyDropTarget) &&
    isEditingComponentDefinition
  ) {
    return Err(
      t(`designer.components.creation-validation-errors.has-drop-target`, {
        target: 'Component',
      })
    );
  }

  // A Slot is always Select-bound; in the definition that drop authors its default, so allow it.
  if (isChildrenBound(rawParent) && !isAuthoringSlotDefault) {
    return Err(
      'To place elements into this element, unlink its content in the settings panel.'
    );
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInside(isPagination)(root)(rawParent)
  ) {
    return Err('Components are not allowed in Pagination.');
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInsideCommerceCartWrapper(rawParent, root)
  ) {
    return Err('Components are not allowed in Cart.');
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInsideCommerceCheckoutFormContainer(rawParent, root)
  ) {
    return Err('Components are not allowed in Checkout Form.');
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInside(isCommercePaypalCheckoutFormContainer)(root)(rawParent)
  ) {
    return Err('Components are not allowed in PayPal Checkout Form.');
  }

  // Don't allow components with forbidden elements (Collection Lists) to be
  // added directly to Rich Text elements.
  if (
    isComponentInst(componentMap)(rawChild) &&
    isInside(isRichText)(root)(rawParent)
  ) {
    const forbiddenElement = findRichTextForbiddenElement(
      rawChild,
      componentMap
    );
    if (forbiddenElement) {
      return Err(
        t('designer.components.component-contains-forbidden-elements', {
          elementType: getRichTextForbiddenElementDisplayName(forbiddenElement),
        })
      );
    }
  }

  // Don't allow components inside Rich Text elements within component definitions.
  if (
    isEditingComponentDefinition &&
    isComponentInst(componentMap)(rawChild) &&
    isInside(isRichText)(root)(rawParent)
  ) {
    return Err(
      t(
        'designer.components.cannot-add-component-to-rich-text-in-component-definition'
      )
    );
  }

  // Don't allow components that contain Collection Lists inside other Collection Lists.
  if (
    containsUserComponentWhichContainsCollectionList(rawChild, componentMap) &&
    isInsideDynamoItem(rawParent, root)
  ) {
    return Err(
      'Components that contain Collection Lists are not allowed inside Collection Lists.'
    );
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInsideAnyCommerceDynListItem(rawParent, root)
  ) {
    return Err('Components are not allowed inside Ecommerce Lists.');
  }

  // Can't allow any elements inside of an unbound dyn list
  // Note - Replace this with Atom Constraints once conditional constraints are in place
  if (isInsideUnboundDynList(root)(rawParent)) {
    return Err(
      'Connect this Collection List to a Collection before adding elements.'
    );
  }

  // Don't allow elements to be dragged inside the main dynlist structure.
  // They should only be allowed inside the dynitem template or empty/loading/error wrappers.
  if (
    (isDynamoWrapper(rawParent) && !isPagination(rawChild)) ||
    isDynamoList(rawParent)
  ) {
    return Err(
      'Drop this element inside the Collection Item element to add it to each Collection item.'
    );
  }

  // Don't allow elements to be dragged inside the locales list structure.
  if (isLocalesList(rawParent)) {
    return Err(
      'Drop this element inside the Locales Item element to add it to each Locales item.'
    );
  }

  if (isComponentInst(componentMap)(rawParent)) {
    const instanceRoot = getComponentInstRootElement(componentMap)(rawParent);
    // we only allow moving elements inside a component instance if it has a slot
    if (!instanceRoot || !selectedElementContainsSlot(instanceRoot)) {
      return Err(
        'Cannot move elements inside a component instance without a slot.'
      );
    }
  }

  if (
    contains(rawChild, isSearchBound) &&
    !contains(rawChild, isSearchResultWrapper) &&
    !isInsideSearchResultItem(root)(rawParent)
  ) {
    return Err(
      'Elements using search result fields cannot be moved outside the search result item.'
    );
  }

  if (
    !contains(rawChild, isSearchForm) &&
    contains(rawChild, isSearchInput) &&
    Expressions.closestElement(root, rawChild.id, isSearchForm) !==
      Expressions.closestElement(root, rawParent.id, isSearchForm)
  ) {
    return Err('Search Input cannot be moved outside the Search Form.');
  }

  if (
    !contains(rawChild, isSearchForm) &&
    contains(rawChild, isSearchButton) &&
    Expressions.closestElement(root, rawChild.id, isSearchForm) !==
      Expressions.closestElement(root, rawParent.id, isSearchForm)
  ) {
    return Err('Search Button cannot be moved outside the Search Form.');
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInsideSearchForm(root)(rawParent)
  ) {
    return Err('Components are not allowed in Search Forms.');
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInsidePasswordPageForm(root)(rawParent)
  ) {
    return Err('Components are not allowed in Password Page Form.');
  }

  if (
    contains(rawChild, isPasswordPageButton) &&
    !isInsidePasswordPageForm(root)(rawParent)
  ) {
    return Err('Password button cannot be moved outside the Password Form.');
  }

  if (
    contains(rawChild, isPasswordPageInput) &&
    !isInsidePasswordPageForm(root)(rawParent)
  ) {
    return Err('Password input cannot be moved outside the Password Form.');
  }

  if (
    contains(rawChild, isSearchResultWrapper) &&
    isEditingComponentDefinition
  ) {
    return Err('Search Result Wrapper is not allowed in Components.');
  }

  if (contains(rawChild, isPasswordPageForm) && isEditingComponentDefinition) {
    return Err('Password Page Form is not allowed in Components.');
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInsideSearchResultWrapper(root)(rawParent)
  ) {
    return Err('Components are not allowed in Search Result Wrapper.');
  }

  if (
    containsComponentInstance(rawChild, componentMap, plugins) &&
    isInsideSearchResultItem(root)(rawParent)
  ) {
    return Err('Components are not allowed in Search Result Items.');
  }

  // Don't allow elements to be dragged inside the search structure (they should only be allowed inside the Empty State or Search Result Item wrappers)
  if (isSearchResultWrapper(rawParent) || isSearchResultList(rawParent)) {
    return Err(
      'Drop this element inside the Search Result Item to add it to each result item, or alternatively inside the Empty State.'
    );
  }

  if (
    containsDynamoWrapperWithDynContextFilter(rawChild) &&
    isEditingComponentDefinition
  ) {
    return Err(
      'Components can’t contain Collection Lists with Current Item filter.'
    );
  }

  if (
    containsDynConditionedWithDynContextCondition(rawChild) &&
    isEditingComponentDefinition
  ) {
    return Err(
      'Elements using “current item” conditional visibility can’t be inside Components.'
    );
  }

  if (
    (containsNestedDynamoWrapper(rawChild) ||
      (isInsideDynamoItem(rawParent, root) &&
        containsDynamoWrapper(rawChild))) &&
    isEditingComponentDefinition
  ) {
    return Err('Nested Collection Lists can’t be inside Components.');
  }

  // Slot content on a page resolves multi-refs against the page's item; only a definition lacks one.
  if (containsItemRefSetBoundList(rawChild) && isEditingComponentDefinition) {
    return Err(
      'Collection Lists that contain multi-references can’t be inside Components.'
    );
  }

  // Don't allow forms or form elements inside search results
  if (
    contains(rawChild, anyPass([isFormWrapper, isFormForm])) &&
    (isSearchResultWrapper(rawParent) ||
      isSearchResultList(rawParent) ||
      isInsideSearchResultItem(root)(rawParent))
  ) {
    return Err('Forms can’t be added to search results.');
  }

  if (isInsideRichTextFigure(root, rawParent)) {
    return Err(
      `Elements cannot be moved inside a Figure within a Rich Text element.`
    );
  }

  if (isDynamoWrapper(rawParent) && isPagination(rawChild)) {
    return isPaginatedDynamoWrapperElement(rawParent)
      ? Ok(rawChild)
      : Err(
          'Pagination is only allowed inside a paginated Collection List Wrapper'
        );
  }

  if (
    !isStandard(rawParent, componentMap) ||
    isLeaf(Plugins.getAtom(plugins, rawParent), plugins)
  ) {
    return Err(
      `Elements cannot be added to ${Expressions.getStringFromDisplayName(
        Plugins.getAtom(plugins, rawParent).displayName
      )} ${
        isUserComponentRoot(componentMap, rawParent) ? 'components' : 'elements'
      }.`
    );
  }

  if (constrainingUserComponents.length > 0) {
    const richTextNestingError = validateStaticRichTextNesting(
      constrainingUserComponents,
      rawChild,
      componentMap
    );
    if (richTextNestingError) {
      return richTextNestingError;
    }

    const constrainedElement = findElementInComponentTree(
      rawChild,
      (element) => {
        if (isCommerceCartWrapper(element)) {
          return isConstrainedByUserComponentUsages(
            'CommerceCartWrapper',
            constrainingUserComponents
          );
        }

        if (isCommerceCheckoutFormContainer(element)) {
          return isConstrainedByUserComponentUsages(
            'CommerceCheckoutFormContainer',
            constrainingUserComponents
          );
        }

        if (isDynamoWrapper(element)) {
          return isConstrainedByUserComponentUsages(
            'DynamoWrapper',
            constrainingUserComponents
          );
        }

        if (isLocalesWrapper(element)) {
          return isConstrainedByUserComponentUsages(
            'LocalesWrapper',
            constrainingUserComponents
          );
        }

        if (isCommerceOrderConfirmationContainer(element)) {
          return isConstrainedByUserComponentUsages(
            'CommerceOrderConfirmationContainer',
            constrainingUserComponents
          );
        }

        if (isCommercePaypalCheckoutFormContainer(element)) {
          return isConstrainedByUserComponentUsages(
            'CommercePaypalCheckoutFormContainer',
            constrainingUserComponents
          );
        }

        if (isUserAccountWrapper(element)) {
          return isConstrainedByUserComponentUsages(
            'UserAccountWrapper',
            constrainingUserComponents
          );
        }

        return false;
      },
      componentMap
    );

    if (constrainedElement) {
      return Err(
        t('designer.components.sitewide-validation-failed', {
          element: Expressions.getStringFromDisplayName(
            Plugins.getAtom(plugins, constrainedElement).displayName
          ),
        })
      );
    }
  }
  // Yey, no errors!
  return Ok(rawChild);
}

const containsUserComponentWhichContainsCollectionList = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): boolean =>
  contains(element, (candidate) => {
    if (!isUserComponentInst(componentMap, candidate)) {
      return false;
    }

    const component = componentMap.get(candidate.type);
    if (!component) {
      return false;
    }

    return expressionContainsSpecificElement(isDynamoWrapper)(
      Component.getRender(component)
    )(componentMap);
  });

function containsComponentInstance(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
) {
  return contains(
    element,
    (candidate) =>
      isUserComponentInst(componentMap, candidate) ||
      isLibraryComponentInstance(candidate, componentMap, plugins)
  );
}

function isLibraryComponentInstance(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): boolean {
  /*
   * Library components are Plugin Components defined on the library plugin
   * that are converted to User Components when added to a Site (with
   * import strategy by 'VALUE'). Therefore we must treat them as User Components
   * when validating nesting constraints.
   */
  const importByValue =
    Plugins.getPluginImportStrategy(plugins, getPluginName(element.type)) ===
    Plugins.IMPORT_STRATEGY.VALUE;
  return isComponentInst(componentMap)(element) && importByValue;
}
